import { useState } from "react";
import { History, Trash2, ArrowUpRight, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { actionLabel } from "@/lib/actions";

function formatTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function HistoryList({ items, onRestore, onDelete, onClear }) {
  return (
    <div className="flex h-full flex-col">
      <div className="mb-3 flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-slate-500" />
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Recent rewrites
          </span>
        </div>
        {items.length > 0 && (
          <button
            data-testid="clear-history-button"
            onClick={onClear}
            className="text-xs font-medium text-slate-500 underline-offset-4 hover:text-red-600 hover:underline"
          >
            Clear all
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="mx-1 flex flex-1 flex-col items-center justify-center rounded-2xl border border-dashed border-[#E6E2D8] bg-white/60 p-8 text-center">
          <Inbox className="mb-3 h-8 w-8 text-slate-300" />
          <p className="text-sm font-medium text-slate-600">No history yet</p>
          <p className="mt-1 text-xs text-slate-400">
            Your rewrites will appear here
          </p>
        </div>
      ) : (
        <div className="thin-scroll flex-1 space-y-2 overflow-y-auto pr-1">
          {items.map((it, idx) => (
            <div
              key={it.id}
              data-testid={`history-item-${idx}`}
              className="group cursor-pointer rounded-xl border border-[#E6E2D8] bg-white p-3 transition-all hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-[0_6px_20px_-8px_rgba(55,48,163,0.25)]"
              onClick={() => onRestore(it)}
            >
              <div className="flex items-center justify-between gap-2">
                <Badge
                  variant="secondary"
                  className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-700 hover:bg-indigo-50"
                >
                  {actionLabel(it.action)}
                </Badge>
                <span className="text-[10px] text-slate-400">
                  {formatTime(it.created_at)}
                </span>
              </div>
              <p className="mt-2 line-clamp-2 text-sm leading-snug text-slate-700">
                {it.output_text}
              </p>
              <div className="mt-2 flex items-center justify-between opacity-0 transition-opacity group-hover:opacity-100">
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600">
                  Restore <ArrowUpRight className="h-3 w-3" />
                </span>
                <button
                  data-testid={`history-delete-button-${idx}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(it.id);
                  }}
                  className="rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                  aria-label="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function HistoryDesktop({ items, onRestore, onDelete, onClear }) {
  return (
    <aside
      data-testid="history-sidebar"
      className="sticky top-6 hidden h-[calc(100vh-3rem)] w-72 flex-shrink-0 rounded-2xl border border-[#E6E2D8] bg-[#F3F1EA]/70 p-4 lg:block xl:w-80"
    >
      <HistoryList
        items={items}
        onRestore={onRestore}
        onDelete={onDelete}
        onClear={onClear}
      />
    </aside>
  );
}

export function HistoryMobileButton({ items, onRestore, onDelete, onClear }) {
  const [open, setOpen] = useState(false);
  const handleRestore = (it) => {
    setOpen(false);
    onRestore(it);
  };
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          data-testid="open-history-mobile"
          variant="outline"
          size="sm"
          className="gap-2 rounded-full border-[#E6E2D8] bg-white"
        >
          <History className="h-4 w-4" />
          <span className="hidden sm:inline">History</span>
          {items.length > 0 && (
            <span className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-indigo-600 px-1.5 text-[10px] font-semibold text-white">
              {items.length}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full border-l border-[#E6E2D8] bg-[#FAF9F5] sm:max-w-sm"
      >
        <SheetHeader className="mb-4">
          <SheetTitle className="font-heading text-xl">Your history</SheetTitle>
          <SheetDescription>
            Tap any item to bring it back into the editor
          </SheetDescription>
        </SheetHeader>
        <div className="h-[calc(100vh-8rem)]">
          <HistoryList
            items={items}
            onRestore={handleRestore}
            onDelete={onDelete}
            onClear={onClear}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
